import { useState, useEffect, useCallback } from 'react';
import type { FormEvent } from 'react';
import { Share2, X, Trash2, AlertCircle, Loader2, Check } from 'lucide-react';
import {
  listMatterCollaborators,
  addMatterCollaborator,
  removeMatterCollaborator,
  type MatterCollaborator,
} from '@/api/client';

import { apiErrorText } from '../../lib/describeApiError';
interface Props {
  open: boolean;
  matterId: string;
  /** Owner's user_id pulled from `matterState.matter.owner_id`.
   *  Used to render the owner row + suppress "remove" buttons. */
  ownerId?: number | null;
  /** Current user — used to (a) check admin to expose mutation
   *  controls and (b) check ownership for the read-only owner view. */
  currentUser?: { id: number; email: string; role: string } | null;
  onClose: () => void;
}

/**
 * Sharing modal for a matter. Renders owner + every explicit
 * collaborator grant. Admins see add/remove controls; non-admins
 * see a read-only list (the API rejects mutations with 403, but
 * we hide the controls here too so the UI doesn't lie).
 */
export default function MatterAccessModal({
  open, matterId, ownerId, currentUser, onClose,
}: Props) {
  const [collabs, setCollabs] = useState<MatterCollaborator[]>([]);
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const isAdmin = currentUser?.role === 'admin';
  const isOwner = (
    typeof ownerId === 'number' &&
    typeof currentUser?.id === 'number' &&
    ownerId === currentUser.id
  );

  const load = useCallback(async () => {
    if (!open || !matterId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listMatterCollaborators(matterId);
      setCollabs(r.collaborators);
      // Cache owner_email when the API surfaced it; otherwise leave
      // null and the render falls back to "User #N".
      setOwnerEmail((r.owner_email as string | null | undefined) ?? null);
    } catch (e: any) {
      setError(apiErrorText(e, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [open, matterId]);

  useEffect(() => { load(); }, [load]);

  // NB-20 — Escape closes the dialog, as the app's other dialogs do.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleAdd = async (ev: FormEvent) => {
    ev.preventDefault();
    const email = addEmail.trim();
    if (!email) return;
    setAdding(true);
    setError(null);
    try {
      await addMatterCollaborator(matterId, { email });
      setAddEmail('');
      setJustAdded(email);
      window.setTimeout(() => setJustAdded(null), 2500);
      await load();
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setError(`No user found with email "${email}".`);
      } else if (status === 400 && detail) {
        setError(detail);
      } else {
        setError(detail || e?.message || 'Could not add collaborator.');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: number) => {
    setRemovingUserId(userId);
    setError(null);
    try {
      await removeMatterCollaborator(matterId, userId);
      await load();
    } catch (e: any) {
      setError(
        apiErrorText(e, 'Could not remove collaborator.'),
      );
    } finally {
      setRemovingUserId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
      onClick={onClose}
      data-testid="matter-access-modal"
    >
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Sharing"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <Share2 size={16} className="text-zinc-500" />
            <h2 className="text-sm font-semibold text-zinc-900">Sharing</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {!isAdmin && (
            <div className="text-[11px] text-zinc-500 leading-snug">
              {isOwner
                ? "You're the owner of this matter. Ask an admin to add or remove collaborators."
                : 'Read-only view. Only admins can manage sharing.'}
            </div>
          )}

          {/* Owner row — always shown when owner_id is available. */}
          {typeof ownerId === 'number' && (
            <section data-testid="matter-access-owner">
              <h3 className="text-[11px] font-medium uppercase tracking-wide text-zinc-500 mb-1.5">
                Owner
              </h3>
              <div className="flex items-center justify-between px-3 py-2 rounded-sm border border-zinc-200 bg-zinc-50">
                <div className="text-sm text-zinc-900">
                  {/* Prefer the email we just fetched; fall back to
                      "You" for self-views; final fallback to the
                      User #N stub for legacy matters where the
                      backend can't resolve an email. */}
                  {ownerEmail
                    ? (isOwner ? `You (${ownerEmail})` : ownerEmail)
                    : (isOwner ? 'You' : `User #${ownerId}`)}
                </div>
                <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">
                  Owner
                </span>
              </div>
            </section>
          )}

          {/* Collaborators */}
          <section data-testid="matter-access-collaborators">
            <h3 className="text-[11px] font-medium uppercase tracking-wide text-zinc-500 mb-1.5">
              Collaborators
            </h3>
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-zinc-500 py-2">
                <Loader2 size={14} className="animate-spin" />
                Loading…
              </div>
            ) : collabs.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-2">
                No collaborators yet.
                {isAdmin && ' Use the form below to add one.'}
              </div>
            ) : (
              <ul className="space-y-1">
                {collabs.map((c) => (
                  <li
                    key={c.user_id}
                    className="flex items-center justify-between px-3 py-2 rounded-sm border border-zinc-200"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-900 truncate">
                        {c.email || `User #${c.user_id}`}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {c.role || 'drafter'}
                        {c.granted_by_email && (
                          <> · added by {c.granted_by_email}</>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleRemove(c.user_id)}
                        disabled={removingUserId === c.user_id}
                        className="text-zinc-400 hover:text-red-600 transition-colors p-1 disabled:opacity-50"
                        title="Remove collaborator"
                        aria-label={`Remove ${c.email || `user ${c.user_id}`}`}
                        data-testid={`remove-collaborator-${c.user_id}`}
                      >
                        {removingUserId === c.user_id
                          ? <Loader2 size={14} className="animate-spin" />
                          : <Trash2 size={14} />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Add (admin only) */}
          {isAdmin && (
            <section data-testid="matter-access-add">
              <h3 className="text-[11px] font-medium uppercase tracking-wide text-zinc-500 mb-1.5">
                Add a collaborator
              </h3>
              <form onSubmit={handleAdd} className="flex gap-2">
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  disabled={adding}
                  className="flex-1 text-sm px-2.5 py-1.5 border border-zinc-300 rounded-sm focus:outline-none focus:border-[#002FA7] disabled:bg-zinc-50"
                  data-testid="matter-access-add-email"
                />
                <button
                  type="submit"
                  disabled={adding || !addEmail.trim()}
                  className="text-sm px-3 py-1.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  data-testid="matter-access-add-submit"
                >
                  {adding ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    'Add'
                  )}
                </button>
              </form>
              <div className="text-[10px] text-zinc-500 mt-1.5">
                The user must already exist in your workspace.
              </div>
              {justAdded && (
                <div className="text-[11px] text-green-700 mt-1.5 inline-flex items-center gap-1">
                  <Check size={12} /> Added {justAdded}
                </div>
              )}
            </section>
          )}

          {error && (
            <div
              className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2"
              data-testid="matter-access-error"
            >
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span className="leading-snug">{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
